import {
  ExpressPlacementBilling,
  ExpressPlacementFeePlusPercentBilling,
  ExpressPlacementFeeType,
  ExpressPlacementFlatBilling,
  ExpressPlacementPercentBilling
} from '../types/expressPlacementBilling';
import { ResponseOrError, wrapError, wrapSuccess } from '../types/responses';

const validatePercentageFee = (
  expressPlacementBilling:
    | ExpressPlacementPercentBilling
    | ExpressPlacementFeePlusPercentBilling
): ResponseOrError<void> => {
  const isInteger = Number.isInteger(expressPlacementBilling.percentAmount);
  const isLessThan100 = expressPlacementBilling.percentAmount < 100;
  const isAtLeast1 = expressPlacementBilling.percentAmount >= 1;

  if (!isInteger || !isLessThan100 || !isAtLeast1) {
    return wrapError(
      new Error(
        'Invalid percent amount in express placement billing configuration'
      )
    );
  }

  return wrapSuccess(undefined);
};

const validateFlatFee = (
  expressPlacementBilling:
    | ExpressPlacementFlatBilling
    | ExpressPlacementFeePlusPercentBilling
): ResponseOrError<void> => {
  const isInteger = Number.isInteger(expressPlacementBilling.flatAmountInCents);
  const isPositive = expressPlacementBilling.flatAmountInCents > 0;
  if (!isInteger || !isPositive) {
    return wrapError(
      new Error(
        'Invalid flat amount in express placement billing configuration'
      )
    );
  }

  return wrapSuccess(undefined);
};

export const validateExpressPlacementBilling = (
  expressPlacementBilling: ExpressPlacementBilling
): ResponseOrError<void> => {
  switch (expressPlacementBilling.feeType) {
    case ExpressPlacementFeeType.FLAT:
      return validateFlatFee(expressPlacementBilling);
    case ExpressPlacementFeeType.PERCENT:
      return validatePercentageFee(expressPlacementBilling);
    case ExpressPlacementFeeType.FLAT_PLUS_PERCENT: {
      const [flatFeeError] = validateFlatFee(expressPlacementBilling);
      if (flatFeeError) {
        return wrapError(flatFeeError);
      }
      const [percentFeeError] = validatePercentageFee(expressPlacementBilling);
      if (percentFeeError) {
        return wrapError(percentFeeError);
      }
      return wrapSuccess(undefined);
    }
    default:
      return wrapError(new Error('Invalid express placement fee type'));
  }
};

/**
 * Pure synchronous helper to compute the express placement fee from a known subtotal.
 * Shared between the invoice-time pricing layer and the subscription-time billing layer
 * so both always use identical arithmetic.
 */
export const computeExpressFeeFromSubtotal = (
  expressPlacementBilling: ExpressPlacementBilling,
  subtotalInCents: number
): ResponseOrError<number> => {
  const [error] = validateExpressPlacementBilling(expressPlacementBilling);
  if (error) {
    return wrapError(error);
  }

  if (expressPlacementBilling.feeType === ExpressPlacementFeeType.FLAT) {
    return wrapSuccess(expressPlacementBilling.flatAmountInCents);
  }

  const expressPercentBasedPlacementFeeInCents = Math.ceil(
    (subtotalInCents * expressPlacementBilling.percentAmount) / 100
  );

  if (expressPlacementBilling.feeType === ExpressPlacementFeeType.PERCENT) {
    return wrapSuccess(expressPercentBasedPlacementFeeInCents);
  }

  const flatPlusPercentBasedPlacementFeeInCents =
    expressPlacementBilling.flatAmountInCents +
    expressPercentBasedPlacementFeeInCents;

  return wrapSuccess(flatPlusPercentBasedPlacementFeeInCents);
};

/**
 * Computes the additive placement fee for invoice pricing.
 * Returns wrapSuccess(0) when the flag is off or the advertiser has no billing config.
 * Returns wrapError if the billing configuration is invalid.
 *
 * @param expressPlacementBilling - The resolved billing config for the advertiser org,
 *   including any value inherited from a parent org.
 */
export const computePlacementFeeForInvoicePricing = (
  enableAdditivePlacementFee: boolean | undefined,
  expressPlacementBilling: ExpressPlacementBilling | null | undefined,
  stripeInvoiceSubtotalInCents: number
): ResponseOrError<number> => {
  if (!enableAdditivePlacementFee || !expressPlacementBilling) {
    return wrapSuccess(0);
  }

  return computeExpressFeeFromSubtotal(
    expressPlacementBilling,
    stripeInvoiceSubtotalInCents
  );
};
